import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import ReactGA from 'react-ga4';

interface AnalyticsContextType {
  trackPageView: (pagePath: string, pageTitle?: string) => void;
  trackEvent: (eventName: string, parameters?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
  gaId?: string;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  gaId = process.env.REACT_APP_GA_MEASUREMENT_ID 
}) => {
  // Initialize Google Analytics only in production
  useEffect(() => {
    if (gaId && process.env.NODE_ENV === 'production') {
      ReactGA.initialize(gaId);
    } else {
      console.log('Analytics disabled - Environment:', process.env.NODE_ENV);
    }
  }, [gaId]);

  const trackPageView = (pagePath: string, pageTitle?: string) => {
    if (process.env.NODE_ENV === 'production') {
      ReactGA.send({ 
        hitType: 'pageview', 
        page: pagePath, 
        title: pageTitle || document.title 
      });
    } else {
      console.log('Analytics (dev): Page view tracked:', { pagePath, pageTitle });
    }
  };

  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (process.env.NODE_ENV === 'production') {
      ReactGA.event(eventName, parameters);
    } else {
      console.log('Analytics (dev): Event tracked:', { eventName, parameters });
    }
  };

  const value = {
    trackPageView,
    trackEvent,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
